import { useEffect, useMemo, useState } from 'react';
import { demeterCalculatorsApi } from '../../../../Apis/Apis';
import {
    AddCalculationEngineInputsResponse,
    CalculationEngineMonthlyValueOverrides,
    FarmerMarginCalculatorModel,
    ListFarmerMarginCalculatorsResponse,
    UpdateCalculationEngineInputsResponse,
} from '../../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import { IIconMenuHorizontalDropdownOption } from '../../../Components/Form/Inputs/IconMenuHorizontalDropdown';
import { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { UpsertCalculationEngine } from '../RiskDefinitions';

type CalculatorSelectInputOption = SelectInputOption<string> & { groupName: string; demeterCalculationEngineGuid: string };

interface UseCustomCalculatorsParameters {
    upsertCalculationEngine: UpsertCalculationEngine;
    setUpsertCalculationEngine: React.Dispatch<React.SetStateAction<UpsertCalculationEngine>>;
    runMonthlyValueOverrides: Array<CalculationEngineMonthlyValueOverrides> | null;
    refreshListFarmerMarginCalculator: () => void;
    listFarmerMarginCalculatorResponse: ListFarmerMarginCalculatorsResponse | undefined;
    selectedCalculator: CalculatorSelectInputOption | undefined;
    handleResetOverrides: () => void;
    addedSuccessfullyMessage: string;
    updatedSuccessfullyMessage: string;
}

interface UseCustomCalculatorsResult {
    selectedSavedCalculatorGuid: string | undefined;
    setSelectedSavedCalculatorGuid: React.Dispatch<React.SetStateAction<string | undefined>>;
    activeSavedCalculator: FarmerMarginCalculatorModel | undefined;
    savedCalculatorOptions: SelectInputOption<string>[];
    handleSave: () => void;
    calculatorFlyoutOptions: IIconMenuHorizontalDropdownOption[];
    isAddCalculatorNameModalOpen: boolean;
    pendingCalculatorName: string;
    setPendingCalculatorName: React.Dispatch<React.SetStateAction<string>>;
    handleOpenSaveAsModal: () => void;
    handleConfirmSaveAs: () => void;
    setIsAddCalculatorNameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isRenameCalculatorModalOpen: boolean;
    pendingRenameCalculatorName: string;
    setPendingRenameCalculatorName: React.Dispatch<React.SetStateAction<string>>;
    handleOpenRenameModal: () => void;
    handleConfirmRename: () => void;
    setIsRenameCalculatorModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isDeleteCalculatorModalOpen: boolean;
    handleOpenDeleteModal: () => void;
    handleConfirmDelete: () => void;
    setIsDeleteCalculatorModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isCreateCalculatorModalOpen: boolean;
    pendingCreateCalculatorName: string;
    setPendingCreateCalculatorName: React.Dispatch<React.SetStateAction<string>>;
    handleConfirmCreate: () => void;
    setIsCreateCalculatorModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const useCustomCalculators = (props: UseCustomCalculatorsParameters): UseCustomCalculatorsResult => {
    const [translations] = useLanguage();
    const [isAddCalculatorNameModalOpen, setIsAddCalculatorNameModalOpen] = useState(false);
    const [pendingCalculatorName, setPendingCalculatorName] = useState('');
    const [isRenameCalculatorModalOpen, setIsRenameCalculatorModalOpen] = useState(false);
    const [pendingRenameCalculatorName, setPendingRenameCalculatorName] = useState('');
    const [isDeleteCalculatorModalOpen, setIsDeleteCalculatorModalOpen] = useState(false);
    const [deleteCalculatorGuidBeingConfirmed, setDeleteCalculatorGuidBeingConfirmed] = useState<string | undefined>();
    const [pendingSelectCalculatorGuid, setPendingSelectCalculatorGuid] = useState<string | undefined>();
    const [selectedSavedCalculatorGuid, setSelectedSavedCalculatorGuid] = useState<string | undefined>();
    const [isCreateCalculatorModalOpen, setIsCreateCalculatorModalOpen] = useState(false);
    const [pendingCreateCalculatorName, setPendingCreateCalculatorName] = useState('');

    const [, refreshAddCalculator, addCalculatorResponse] = useApiWithoutAutoExecute<void, AddCalculationEngineInputsResponse>(
        () =>
            demeterCalculatorsApi.addCalculationEngineInputs(props.upsertCalculationEngine.demeterCalculationEngineGuid, {
                ...props.upsertCalculationEngine,
                monthlyValueOverrides: props.runMonthlyValueOverrides,
            }),
        {
            successMessage: props.addedSuccessfullyMessage,
        },
    );

    const [, refreshUpdateCalculator, updateCalculatorResponse] = useApiWithoutAutoExecute<void, UpdateCalculationEngineInputsResponse>(
        () =>
            demeterCalculatorsApi.updateCalculationEngineInputs(
                props.upsertCalculationEngine.demeterCalculationEngineGuid!,
                props.upsertCalculationEngine.demeterCalculationEngineInputsGuid ?? '',
                { ...props.upsertCalculationEngine, monthlyValueOverrides: props.runMonthlyValueOverrides },
            ),
        {
            successMessage: props.updatedSuccessfullyMessage,
        },
    );

    const [, refreshDeleteCalculator, deleteCalculatorResponse] = useApiWithoutAutoExecute(
        () => demeterCalculatorsApi.deleteCalculationEngineInputs(props.selectedCalculator!.demeterCalculationEngineGuid, deleteCalculatorGuidBeingConfirmed!),
        {
            successMessage: translations.risk.margin.messages.customCalculatorDeleteSuccess,
        },
    );

    // Override data (valueOverrides, textOverrides, monthlyValueOverrides) is sourced from the list response;
    // the GET endpoint returns empty overrides when called without an inputs guid.
    const activeSavedCalculator =
        selectedSavedCalculatorGuid != null
            ? props.listFarmerMarginCalculatorResponse?.rows?.find((x) => x.demeterCalculationEngineInputsGuid === selectedSavedCalculatorGuid)
            : props.listFarmerMarginCalculatorResponse?.rows?.find(
                  (x) => x.demeterCalculationEngineGuid === props.selectedCalculator?.demeterCalculationEngineGuid && (x.name == null || x.name.trim() === ''),
              );

    const savedCalculatorOptions = useMemo(() => {
        const customCalculators =
            props.listFarmerMarginCalculatorResponse?.rows
                ?.filter((row) => row.name != null && row.name.trim() !== '')
                .map((row) => ({
                    label: `${row.name} - ${row.groupName}`,
                    value: row.demeterCalculationEngineInputsGuid ?? '',
                })) ?? [];

        if (customCalculators.length === 0) {
            return [];
        }

        return [{ label: translations.risk.margin.text.customCalculatorNone, value: '' }, ...customCalculators];
    }, [props.listFarmerMarginCalculatorResponse]);

    useEffect(() => {
        if (!addCalculatorResponse && !updateCalculatorResponse) {
            return;
        }

        if (addCalculatorResponse?.demeterCalculationEngineInputsGuid) {
            setPendingSelectCalculatorGuid(addCalculatorResponse.demeterCalculationEngineInputsGuid);
        }

        props.refreshListFarmerMarginCalculator();
    }, [addCalculatorResponse, updateCalculatorResponse]);

    useEffect(() => {
        if (!deleteCalculatorResponse) {
            return;
        }

        setSelectedSavedCalculatorGuid(undefined);
        setDeleteCalculatorGuidBeingConfirmed(undefined);
        props.setUpsertCalculationEngine({ ...props.upsertCalculationEngine, demeterCalculationEngineInputsGuid: '' });
        props.refreshListFarmerMarginCalculator();
    }, [deleteCalculatorResponse]);

    useEffect(() => {
        if (!pendingSelectCalculatorGuid || !props.listFarmerMarginCalculatorResponse?.rows?.length) {
            return;
        }

        const newSave = props.listFarmerMarginCalculatorResponse.rows.find((x) => x.demeterCalculationEngineInputsGuid === pendingSelectCalculatorGuid);
        if (!newSave) {
            return;
        }

        setSelectedSavedCalculatorGuid(pendingSelectCalculatorGuid);
        setPendingSelectCalculatorGuid(undefined);
    }, [props.listFarmerMarginCalculatorResponse, pendingSelectCalculatorGuid]);

    const handleSave = () => {
        if (!activeSavedCalculator) {
            // Empty name marks this as a company-shared save (custom calculators have a user-given name).
            props.setUpsertCalculationEngine({ ...props.upsertCalculationEngine, name: '' });
            refreshAddCalculator();
        } else {
            // Preserve the existing save's name so its scope (company-shared vs custom) is unchanged.
            props.setUpsertCalculationEngine({ ...props.upsertCalculationEngine, name: activeSavedCalculator.name ?? '' });
            refreshUpdateCalculator();
        }
    };

    const handleOpenSaveAsModal = () => {
        setPendingCalculatorName('');
        setIsAddCalculatorNameModalOpen(true);
    };

    const handleConfirmSaveAs = () => {
        props.setUpsertCalculationEngine({ ...props.upsertCalculationEngine, name: pendingCalculatorName });
        refreshAddCalculator();
        setIsAddCalculatorNameModalOpen(false);
    };

    const handleOpenRenameModal = () => {
        setPendingRenameCalculatorName(activeSavedCalculator?.name ?? '');
        setIsRenameCalculatorModalOpen(true);
    };

    const handleConfirmRename = () => {
        props.setUpsertCalculationEngine({ ...props.upsertCalculationEngine, name: pendingRenameCalculatorName });
        refreshUpdateCalculator();
        setIsRenameCalculatorModalOpen(false);
    };

    const handleOpenDeleteModal = () => {
        setDeleteCalculatorGuidBeingConfirmed(selectedSavedCalculatorGuid);
        setIsDeleteCalculatorModalOpen(true);
    };

    const handleConfirmDelete = () => {
        refreshDeleteCalculator();
        setIsDeleteCalculatorModalOpen(false);
    };

    const handleOpenCreateModal = () => {
        setPendingCreateCalculatorName('');
        setIsCreateCalculatorModalOpen(true);
    };

    const handleConfirmCreate = () => {
        props.handleResetOverrides();
        props.setUpsertCalculationEngine({
            ...props.upsertCalculationEngine,
            name: pendingCreateCalculatorName,
            valueOverrides: {},
            demeterCalculationEngineInputsGuid: '',
        });
        refreshAddCalculator();
        setIsCreateCalculatorModalOpen(false);
    };

    const calculatorFlyoutOptions: IIconMenuHorizontalDropdownOption[] = [
        {
            key: 'createFromScratch',
            label: translations.risk.margin.text.customCalculatorNewEmpty,
            handleClick: handleOpenCreateModal,
        },
        {
            key: 'cloneCurrentCalculator',
            label: translations.risk.margin.text.customCalculatorCloneCurrent,
            handleClick: handleOpenSaveAsModal,
        },
    ];

    return {
        selectedSavedCalculatorGuid,
        setSelectedSavedCalculatorGuid,
        activeSavedCalculator,
        savedCalculatorOptions,
        handleSave,
        calculatorFlyoutOptions,
        isAddCalculatorNameModalOpen,
        pendingCalculatorName,
        setPendingCalculatorName,
        handleOpenSaveAsModal,
        handleConfirmSaveAs,
        setIsAddCalculatorNameModalOpen,
        isRenameCalculatorModalOpen,
        pendingRenameCalculatorName,
        setPendingRenameCalculatorName,
        handleOpenRenameModal,
        handleConfirmRename,
        setIsRenameCalculatorModalOpen,
        isDeleteCalculatorModalOpen,
        handleOpenDeleteModal,
        handleConfirmDelete,
        setIsDeleteCalculatorModalOpen,
        isCreateCalculatorModalOpen,
        pendingCreateCalculatorName,
        setPendingCreateCalculatorName,
        handleConfirmCreate,
        setIsCreateCalculatorModalOpen,
    };
};

export default useCustomCalculators;
